import React, { Fragment, useEffect, useState } from 'react';
import { BOTTOM_INDENTS, COLORS, ROUTE_PATHS } from '@constants';
import { PURCHASE_EVENT_STATUS, SCRIPT_TAG_STATUS } from '@enums';
import { useNotification, usePolling, useStore } from '@hooks';
import { Loader, RedirectLink, Typography } from '@ui';
import { observer } from 'mobx-react';
import IntegrationBig from './components/IntegrationBig';
import IntegrationLoop from './components/IntegrationLoop';
import IntegrationRecharge from './components/IntegrationRecharge';
import IntegrationRequest from './components/IntegrationRequest';
import IntegrationShopify from './components/IntegrationShopify';
import StoreSetupManual from './components/StoreSetupManual';

const Integrations = observer(() => {
    const store = useStore();
    const notification = useNotification();
    const { isShopify, isManual, isBigCommerce, setupInfo, getSetupInfo } = store.accountsStore;

    const {
        rechargeRedirectUrl,
        hasRechargeToken,
        isRechargeConnected,
        accountHasAllScopes,
        getRechargeRedirect,
        getRechargeToken,
        getLoopToken,
        handleLoopToken,
        loopAccessToken,
        isLoopConnected
    } = store.integrationsStore;

    const domains = store.accountsStore?.currentAccount?.domains || [];
    const domain = domains.find((domain) => domain.class === 1)?.shopDomain || '';

    const [loading, setLoading] = useState(true);
    const [isPolling, setIsPolling] = useState(false);
    const [modal, setModal] = useState(null);
    const [closeModalFn, setCloseModalFn] = useState(null);

    const handleModalClose = (closeFn) => {
        setCloseModalFn(() => closeFn);
    };

    const { startPolling, stopPolling } = usePolling(getSetupInfo, 10000, false, 2000);

    useEffect(() => {
        (async () => {
            await Promise.all([
                isShopify || isBigCommerce ? getRechargeToken() : null,
                (isShopify || isBigCommerce) && !rechargeRedirectUrl ? getRechargeRedirect() : null,
                isShopify ? getLoopToken() : null
            ]);

            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        const isScriptTagStatusPending = setupInfo.scriptTag.status === SCRIPT_TAG_STATUS.PENDING;
        if (setupInfo.purchaseEventStatus === PURCHASE_EVENT_STATUS.PENDING || isScriptTagStatusPending) {
            startPolling();
            if (isScriptTagStatusPending) setIsPolling(true);
        } else {
            stopPolling();
        }
    }, [setupInfo.purchaseEventStatus, setupInfo.scriptTag.status]);

    useEffect(() => {
        const scriptTagStatus = setupInfo.scriptTag.status;

        if (isPolling && scriptTagStatus !== SCRIPT_TAG_STATUS.PENDING) {
            if (scriptTagStatus === SCRIPT_TAG_STATUS.CONNECTED) {
                if (closeModalFn) {
                    closeModalFn();
                }
                setModal(null);
                notification.success({
                    message: 'Script tag was successfully connected',
                    description: (
                        <p>
                            You are now eligible to{' '}
                            <RedirectLink
                                replace
                                display='inline'
                                text='run your campaigns'
                                to={ROUTE_PATHS.CAMPAIGNS}
                            />
                        </p>
                    )
                });
            }

            if (scriptTagStatus === SCRIPT_TAG_STATUS.NOT_CONNECTED) {
                notification.error({
                    message: 'We couldn\'t validate Klickly tag setup', // eslint-disable-line
                    description: 'Please ensure script tag was installed and verify connection'
                });
            }

            stopPolling();
            setIsPolling(false);
        }
    }, [setupInfo.scriptTag.status, isPolling]);

    if (loading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Typography.Title data-e2e='integrations_page_title' level={2} style={BOTTOM_INDENTS.M}>
                Integrations
            </Typography.Title>
            <Typography.Text
                data-e2e='integrations_page_description'
                type='body2'
                color={COLORS.$gray60Black}
                style={BOTTOM_INDENTS.M}
            >
                Integrations let you streamline product upload, user tracking, and order fulfillment.
            </Typography.Text>
            {isManual && <StoreSetupManual modal={modal} setModal={setModal} onModalClose={handleModalClose} />}
            {isBigCommerce && (
                <IntegrationBig
                    modal={modal}
                    setModal={setModal}
                    onModalClose={handleModalClose}
                    storeUrl={domain}
                    isValid
                />
            )}
            {isShopify && (
                <IntegrationShopify
                    modal={modal}
                    setModal={setModal}
                    onModalClose={handleModalClose}
                    storeUrl={domain}
                    isValid={accountHasAllScopes}
                />
            )}
            {(isShopify || isBigCommerce) && hasRechargeToken && (
                <IntegrationRecharge isConnected={isRechargeConnected} redirectUrl={rechargeRedirectUrl} />
            )}
            {isShopify && isLoopConnected && (
                <IntegrationLoop isConnected={isLoopConnected} token={loopAccessToken} onSubmit={handleLoopToken} />
            )}
            <IntegrationRequest />
        </Fragment>
    );
});

export default Integrations;
